/**
 * ActivityInspector (Sprint 4.3.2, PROD-651).
 *
 * Right-side inspector that opens when the user clicks an Action node
 * IN RUNTIME MODE. Distinct from the design-time `<PathwayInspector>`
 * which surfaces editor properties (label / description / state-machine
 * / lab basket / override). The ActivityInspector renders the runtime
 * orchestration view per the Awell ontology:
 *   - `activity_id`
 *   - `status` (closed 6-value enum)
 *   - `source_trigger_ref`
 *   - `start_date` / `completion_date`
 *   - `indirect_object` (assignee)
 *
 * Multiplicity: when the canvas backs the action with N > 1 activities
 * (FHIR Tasks instantiated from the same action def), the inspector
 * shows the activity list and lets the operator inspect any one of
 * them. The default selection is the most recently started activity.
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/docs/
 *   getting-started/orchestration-ontology.md
 *   ("an activity is created in the Awell System to keep track of its
 *    start date, status, completion date, etc.")
 *
 * HC2 token-only: every color resolves through Signal DS Tailwind
 * tokens. The 6 status enum values map onto distinct token
 * combinations (see STATUS_PILL_CLASSES) so screen-reader users get an
 * `aria-label` on the pill and color-vision-safe users get a
 * distinguishable shape via the ring + fill contrast.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: the inspector is `role="region"` with an `aria-labelledby`
 * pointing at the inspector title. The status pill carries
 * `aria-label="Activity status, completed"` etc.
 */
import {
  PATHWAY_ACTIVITY_STATUSES,
  type PathwayActivity,
  type PathwayActivityStatus,
} from '../../types/pathway-activity';
import { ActivityCard } from './ActivityCard';
import type { ActivityIconType } from './icons/ActivityTypeIcon';

/**
 * Best-effort heuristic mapping an activity (action_def_id slug) onto
 * the closed 6-type activity icon vocabulary. Used by the inspector to
 * embed the `<ActivityCard>` VERB header at the top of the focused
 * panel. The heuristic is intentionally conservative; when the
 * action_def_id encodes the type explicitly the icon resolves
 * deterministically, otherwise the fallback `'form'` icon renders.
 *
 * Sprint 4.3.3 callers will eventually wire a real activity-type field
 * onto `PathwayActivity`; this heuristic keeps the prototype rendering
 * a sensible icon until that field lands.
 */
function inferActivityIconType(actionDefId: string): ActivityIconType {
  const id = actionDefId.toLowerCase();
  if (id.includes('api') || id.includes('emr') || id.includes('http'))
    return 'api_call';
  if (
    id.includes('calc') ||
    id.includes('score') ||
    id.includes('risk-engine')
  )
    return 'calculation';
  if (id.includes('checklist') || id.includes('safety')) return 'checklist';
  if (id.includes('reminder') || id.includes('nudge')) return 'reminder';
  if (id.includes('message') || id.includes('comm')) return 'message';
  return 'form';
}

export interface ActivityInspectorProps {
  /** Action node the inspector is bound to (for the title / context). */
  actionId: string;
  actionLabel: string;
  /** All activities backing this action. Empty array = "no runtime data". */
  activities: PathwayActivity[];
  className?: string;
}

const EMPTY_STATE_MESSAGE =
  'No runtime activities for this action yet. Activities appear when the orchestrator activates the action.';

/**
 * Token-driven palette per status. HC2: every value resolves to an
 * existing Signal DS token. The combinations are deliberately distinct
 * (background + foreground + ring) so a color-vision-safe operator can
 * tell the 6 states apart without relying on hue alone.
 *
 *   - pending   -> signal-primary-100 fill, signal-primary-700 text
 *   - active    -> status-active-bg fill, status-active-fg text
 *   - completed -> status-active filled solid (text-inverse on default)
 *                  so the visual contrast separates it from `active`
 *                  even though both belong to the green family
 *   - skipped   -> surface-muted with text-tertiary, ring-surface-border
 *   - failed    -> warning-strong-bg fill, warning-strong-fg text
 *   - expired   -> surface-muted with text-tertiary, ring dashed
 */
export const STATUS_PILL_CLASSES: Record<PathwayActivityStatus, string> = {
  pending:
    'bg-signal-primary-100 text-signal-primary-700 ring-1 ring-signal-primary-300',
  active:
    'bg-status-active-bg text-status-active-fg ring-1 ring-status-active',
  completed:
    'bg-status-active text-text-inverse ring-1 ring-status-active-fg',
  skipped:
    'bg-surface-muted text-text-tertiary ring-1 ring-surface-border',
  failed:
    'bg-warning-strong-bg text-warning-strong-fg ring-1 ring-warning-strong-border',
  expired:
    'bg-surface-muted text-text-tertiary ring-1 ring-surface-border ring-dashed',
};

const STATUS_LABEL: Record<PathwayActivityStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  completed: 'Completed',
  skipped: 'Skipped',
  failed: 'Failed',
  expired: 'Expired',
};

/** Pure display helper for the timestamp rows. Renders an em-dash-free fallback. */
function formatTimestamp(iso: string | null): string {
  if (iso === null || iso.length === 0) return 'Not yet';
  return iso;
}

export function ActivityInspector({
  actionId,
  actionLabel,
  activities,
  className,
}: ActivityInspectorProps) {
  // Pick the most recently started activity as the active row when N >= 1.
  // Falls back to the first entry when start_date is missing.
  const sorted = [...activities].sort((a, b) => {
    const aTs = a.start_date ?? '';
    const bTs = b.start_date ?? '';
    return bTs.localeCompare(aTs);
  });
  const focused = sorted[0] ?? null;

  return (
    <aside
      role="region"
      aria-labelledby="activity-inspector-title"
      data-testid="activity-inspector"
      data-action-id={actionId}
      data-activity-count={activities.length}
      className={[
        'flex w-80 flex-col gap-gutter-md border-l border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div>
        <h3
          id="activity-inspector-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Activity Inspector
        </h3>
        <p className="mt-1 text-signal-sm text-text-primary">{actionLabel}</p>
      </div>
      {focused === null ? (
        <p
          data-testid="activity-inspector-empty-state"
          className="text-signal-sm text-text-tertiary"
        >
          {EMPTY_STATE_MESSAGE}
        </p>
      ) : (
        <ActivityFocusedPanel focused={focused} />
      )}
      {activities.length > 1 ? (
        <ActivityListPanel activities={sorted} focusedId={focused?.activity_id} />
      ) : null}
    </aside>
  );
}

interface ActivityFocusedPanelProps {
  focused: PathwayActivity;
}

function ActivityFocusedPanel({ focused }: ActivityFocusedPanelProps) {
  const iconType = inferActivityIconType(focused.action_def_id);
  // Sprint 4.3.3 (PROD-652): VERB-shape header. Subject is always the
  // pathway runtime per the Awell ontology; action verb is derived from
  // the activity status; object is the activity label / id; indirect
  // object is the assignee.
  const action =
    focused.status === 'completed'
      ? 'completed'
      : focused.status === 'active' || focused.status === 'pending'
        ? 'assigned'
        : focused.status;
  return (
    <div
      data-testid={`activity-inspector-focused-${focused.activity_id}`}
      className="flex flex-col gap-gutter-sm"
    >
      <ActivityCard
        activityType={iconType}
        subject={{ name: 'Care Pathway Engine', type: 'system' }}
        action={action}
        object={{
          name: focused.label ?? focused.activity_id,
          type: focused.action_def_id,
        }}
        indirectObject={{
          name: focused.indirect_object,
          type: 'stakeholder',
        }}
      />
      <ActivityStatusPill status={focused.status} />
      <DataRow label="Activity id" value={focused.activity_id} />
      <DataRow label="Source trigger" value={focused.source_trigger_ref} />
      <DataRow label="Start date" value={formatTimestamp(focused.start_date)} />
      <DataRow
        label="Completion date"
        value={formatTimestamp(focused.completion_date)}
      />
      <DataRow label="Assignee" value={focused.indirect_object} />
    </div>
  );
}

interface ActivityListPanelProps {
  activities: PathwayActivity[];
  focusedId: string | undefined;
}

function ActivityListPanel({ activities, focusedId }: ActivityListPanelProps) {
  return (
    <div
      data-testid="activity-inspector-list"
      className="flex flex-col gap-1 border-t border-surface-border pt-gutter-sm"
    >
      <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        All activities
      </span>
      <ul className="flex flex-col gap-1">
        {activities.map((a) => (
          <li
            key={a.activity_id}
            data-testid={`activity-inspector-row-${a.activity_id}`}
            data-focused={a.activity_id === focusedId ? 'true' : 'false'}
            className="flex items-center justify-between gap-gutter-sm rounded-signal-md bg-surface-base px-gutter-sm py-1"
          >
            <span className="text-signal-xs text-text-secondary">
              {a.activity_id}
            </span>
            <ActivityStatusPill status={a.status} compact />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ActivityStatusPillProps {
  status: PathwayActivityStatus;
  compact?: boolean;
}

export function ActivityStatusPill({
  status,
  compact = false,
}: ActivityStatusPillProps) {
  const label = STATUS_LABEL[status];
  return (
    <span
      role="status"
      aria-label={`Activity status, ${label}`}
      data-testid={`activity-status-pill-${status}`}
      data-status={status}
      className={[
        'inline-flex w-fit items-center rounded-signal-pill font-semibold uppercase tracking-wider',
        compact
          ? 'px-gutter-sm py-0.5 text-signal-xs'
          : 'px-gutter-md py-0.5 text-signal-xs',
        STATUS_PILL_CLASSES[status],
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label}
    </span>
  );
}

interface DataRowProps {
  label: string;
  value: string;
}

function DataRow({ label, value }: DataRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span className="text-signal-sm text-text-primary">{value}</span>
    </div>
  );
}

export {
  EMPTY_STATE_MESSAGE,
  PATHWAY_ACTIVITY_STATUSES as ACTIVITY_INSPECTOR_STATUSES,
};
